import React, { useState } from "react";
import {
  CheckCheck, ChevronRight, ListChecks, Pencil, Play, Plus, Rows3, Search, Trash2, History,
} from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { AgentPage, BrandButton, GhostButton, ScoreBar, Sparkline } from "@/shared/components/ui-kit";
import { fmtCost } from "@/shared/lib/runs";
import { allTags, checksFor, verdict } from "@/shared/lib/evals-model";
import useSuite from "./evals/useSuite";
import Matrix from "./evals/Matrix";
import CaseSheet from "./evals/CaseSheet";
import CaseDialog, { CheckList } from "./evals/CaseDialog";
import { StatusPill, DeltaChip, TagChip } from "./evals/atoms";

import type { ReactNode } from "react";

import type {
  Agent, EvalCase, EvalCaseOrigin, EvalResult, EvalRun, EvalSuite, EvalVerdict,
} from "@/shared/types";
import { StatTile } from "@/shared/components/StatTile";

/* Screen 1 — the suite. Mirrors TracesPage's shape exactly (AgentPage list ⇄
   full-bleed detail) so Evals and Traces read as siblings rather than as two
   people's ideas of what a page is.

   The whole tab answers one question: if I change this agent, does it get
   better or worse? Everything here serves that and nothing else. */

const MODES = [
  ["cases", "Cases", Rows3],
  ["runs", "Runs", History],
  ["checks", "Checks", ListChecks],
] as const;

/** Which list the suite screen is showing. */
type Mode = (typeof MODES)[number][0];

function ModePicker({ mode, setMode, caseCount, runCount }: {
  mode: Mode;
  setMode: (mode: Mode) => void;
  caseCount: number;
  runCount: number;
}) {
  const counts: Partial<Record<Mode, number>> = { cases: caseCount, runs: runCount };
  return (
    <div className="inline-flex items-center p-[3px] rounded-full bg-[var(--surface-2)] mb-4">
      {MODES.map(([id, label, Icon]) => (
        <button
          key={id}
          onClick={() => setMode(id)}
          className={`press flex items-center gap-1.5 h-7 px-3 rounded-full text-[12.5px] font-medium ${
            mode === id
              ? "bg-[var(--surface)] text-[var(--text-primary)] ring-1 ring-[var(--hairline)] shadow-lift"
              : "text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
          }`}
        >
          <Icon className="w-3 h-3" strokeWidth={1.75} />
          {label}
          {counts[id] != null && (
            <span className="font-mono text-[11px] tabular-nums text-[var(--text-quaternary)]">{counts[id]}</span>
          )}
        </button>
      ))}
    </div>
  );
}

const VERDICT_TONE: Record<EvalVerdict, string> = {
  improvement: "text-[var(--success)]",
  regression: "text-[var(--danger)]",
  tie: "text-[var(--text-tertiary)]",
  none: "text-[var(--text-quaternary)]",
};

/* A server run's counts arrive before its results; a local run has only results. */
const passedOf = (run: EvalRun) => run.passed ?? run.results.filter((r) => r.status === "pass").length;
const totalOf = (run: EvalRun) => run.total ?? run.results.length;

function StatsRow({ evals, latest, base }: {
  evals: EvalSuite;
  latest: EvalRun | null;
  base: EvalRun | null;
}) {
  const v = verdict(latest?.score, base?.score);
  const d = latest?.score != null && base?.score != null ? latest.score - base.score : null;
  // a run still going, or one that produced no score, is no point on a trend
  const trend = [...evals.runs].reverse().flatMap((r) => (r.score == null ? [] : [r.score]));
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-1 p-1 rounded-[18px] bg-[var(--surface-2)] mb-5">
      <StatTile
        label="Score"
        value={latest?.score == null ? "—" : latest.score.toFixed(2)}
        sub={latest ? `${latest.target.version} · ${latest.when}` : "Never run"}
      >
        {trend.length > 1 && <Sparkline data={trend} width={60} height={24} baseline="min" dot className="text-[var(--text-quaternary)]" />}
      </StatTile>
      <StatTile
        label="Passing"
        value={latest ? `${passedOf(latest)}/${totalOf(latest)}` : `0/${evals.cases.length}`}
        sub={latest ? (latest.status === "running" ? "grading…" : `${Math.round(latest.passRate * 100)}% of cases`) : `${evals.cases.length} cases ready`}
      />
      <StatTile
        label="vs baseline"
        value={d == null ? "—" : `${d > 0 ? "+" : d < 0 ? "−" : ""}${Math.abs(d).toFixed(2)}`}
        sub={base ? `${{ improvement: "Improved on", regression: "Regressed from", tie: "Level with", none: "" }[v]} ${base.target.version}` : "No baseline set"}
        tone={VERDICT_TONE[v]}
      />
      <StatTile
        label="Cost per run"
        value={latest ? fmtCost(latest.cost) : "—"}
        sub={`${evals.checks.length} suite-wide check${evals.checks.length === 1 ? "" : "s"}`}
      />
    </div>
  );
}

/* Score across runs, oldest → newest. Only drawn once there are two runs to
   join — a single point is a dot pretending to be a trend. */
function TrendChart({ runs }: { runs: EvalRun[] }) {
  const data = [...runs].reverse().filter((r) => r.score != null).map((r) => ({ name: r.target.version, score: r.score ?? 0, when: r.when }));
  if (data.length < 2) return null;
  const AXIS = { tickLine: false, axisLine: false, tick: { fontSize: 11, fill: "var(--text-tertiary)" } };
  return (
    <div className="rounded-[14px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] shadow-lift p-4 mb-5">
      <div className="section-label mb-1">Score over versions</div>
      <div className="h-[160px] px-1 pb-1">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 12, right: 8, left: -20, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke="var(--hairline)" />
            <XAxis dataKey="name" {...AXIS} />
            <YAxis domain={[0, 1]} ticks={[0, 0.5, 1]} {...AXIS} />
            <Tooltip
              cursor={{ stroke: "var(--hairline-strong)" }}
              contentStyle={{
                background: "var(--canvas)", border: "1px solid var(--hairline-strong)",
                borderRadius: 10, fontSize: 12, padding: "6px 9px",
              }}
              // Recharts hands a formatter its widest value type; this series
              // is the numeric score.
              formatter={(v) => [(v as number).toFixed(2), "Score"]}
            />
            <Line type="monotone" dataKey="score" stroke="var(--text-primary)" strokeWidth={1.75} dot={{ r: 2.5, fill: "var(--text-primary)" }} activeDot={{ r: 3.5 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function CaseRow({ kase, evals, result, onOpen, onEdit, onDelete }: {
  kase: EvalCase;
  evals: EvalSuite;
  result: EvalResult | null;
  onOpen: (kase: EvalCase) => void;
  onEdit: (kase: EvalCase) => void;
  onDelete: (kase: EvalCase) => void;
}) {
  const checkCount = checksFor(evals, kase).length;
  return (
    <div className="group flex items-center gap-3 px-3 py-2.5 hover:bg-[var(--accent-hover)] transition-colors">
      <button onClick={() => onOpen(kase)} className="press flex-1 min-w-0 flex items-center gap-3 text-left">
        <span className="flex-1 min-w-0">
          <span className="block text-[13.5px] font-medium text-[var(--text-primary)] tracking-[-0.005em] truncate">
            {kase.name}
          </span>
          <span className="flex items-center gap-1.5 mt-[3px]">
            <span className="text-[11.5px] text-[var(--text-quaternary)] font-mono">{checkCount} checks</span>
            {(kase.tags || []).map((t) => <TagChip key={t} tag={t} />)}
          </span>
        </span>
        <span className="hidden sm:block w-[92px] shrink-0">
          {result ? <ScoreBar value={result.score} target={0.7} width={40} /> : <span className="font-mono text-[11.5px] text-[var(--text-quaternary)]">not run</span>}
        </span>
        <span className="w-[70px] shrink-0 text-right">
          {result ? <StatusPill status={result.status} /> : null}
        </span>
      </button>
      {/* Row actions swap in on hover with no layout shift — the house pattern. */}
      <span className="shrink-0 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
        <button onClick={() => onEdit(kase)} title="Edit case" className="press w-7 h-7 rounded-[7px] flex items-center justify-center text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--accent-hover)]">
          <Pencil className="w-3.5 h-3.5" strokeWidth={1.75} />
        </button>
        <button onClick={() => onDelete(kase)} title="Delete case" className="press w-7 h-7 rounded-[7px] flex items-center justify-center text-[var(--text-tertiary)] hover:text-[var(--danger)] hover:bg-[var(--accent-hover)]">
          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />
        </button>
      </span>
      <ChevronRight className="w-3.5 h-3.5 shrink-0 text-[var(--text-quaternary)]" strokeWidth={1.75} />
    </div>
  );
}

function RunRow({ run, base, isBaseline, onOpen, onSetBaseline }: {
  run: EvalRun;
  base: EvalRun | null;
  isBaseline: boolean;
  onOpen: (id: string) => void;
  onSetBaseline: (id: string) => void;
}) {
  const d = base && run.score != null && base.score != null && base.id !== run.id ? +(run.score - base.score).toFixed(3) : null;
  return (
    <div className="group flex items-center gap-3 px-3 py-2.5 hover:bg-[var(--accent-hover)] transition-colors">
      <button onClick={() => onOpen(run.id)} className="press flex-1 min-w-0 flex items-center gap-3 text-left">
        <span className="flex-1 min-w-0">
          <span className="flex items-center gap-2">
            <span className="font-mono text-[12px] font-medium text-[var(--text-primary)]">{run.target.version}</span>
            {isBaseline && (
              <span className="shrink-0 h-5 px-2 rounded-full text-[11.5px] font-medium flex items-center bg-[var(--info-bg)] text-[var(--info-text)]">
                Baseline
              </span>
            )}
          </span>
          <span className="block text-[12.5px] text-[var(--text-tertiary)] mt-[1px] truncate">{run.label}</span>
        </span>
        <span className="hidden sm:block w-[92px] shrink-0"><ScoreBar value={run.score} target={0.7} width={40} /></span>
        <span className="w-[60px] shrink-0 text-center"><DeltaChip delta={d} /></span>
        <span className="hidden md:block w-[64px] shrink-0 font-mono text-[12px] tabular-nums text-[var(--text-secondary)] text-right">
          {fmtCost(run.cost)}
        </span>
        <span className="w-[80px] shrink-0 text-[12px] text-[var(--text-tertiary)] text-right">{run.when}</span>
      </button>
      <span className="shrink-0 w-[92px] flex justify-end opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
        {!isBaseline && (run.status ?? "done") === "done" && (
          <button
            onClick={() => onSetBaseline(run.id)}
            title="Measure every later run against this one"
            className="press h-7 px-2.5 rounded-full text-[11.5px] font-medium ring-1 ring-[var(--hairline-strong)] text-[var(--text-secondary)] hover:bg-[var(--accent-hover)]"
          >
            Baseline
          </button>
        )}
      </span>
    </div>
  );
}

/* ---------------------------------- page ---------------------------------- */

/** Which case is open in the sheet, in which run, against which baseline. */
interface SheetTarget {
  caseId: string;
  runId: string;
  baseId: string | null;
}

export default function EvalsPage({ agent, onOpenTrace }: {
  agent: Agent | null;
  onOpenTrace: (origin: EvalCaseOrigin) => void;
}) {
  const [mode, setMode] = useState<Mode>("cases");
  const [query, setQuery] = useState("");
  const [tag, setTag] = useState("all");
  const [openRunId, setOpenRunId] = useState<string | null>(null);
  // null: the matrix compares with the baseline; "" compares with nothing
  const [compareId, setCompareId] = useState<string | null>(null);
  const [sheet, setSheet] = useState<SheetTarget | null>(null);
  const [editing, setEditing] = useState<EvalCase | "new" | null>(null);

  const openRun = (id: string | null) => {
    setOpenRunId(id);
    setCompareId(null);
  };
  const suite = useSuite(agent, {
    load: [openRunId, compareId, sheet?.runId, sheet?.baseId],
    onFinished: (run) => openRun(run.id),
  });
  const { evals, progress } = suite;
  if (!agent) return null;

  const latest = evals.runs[0] || null;
  const base = evals.runs.find((r) => r.id === evals.baselineRunId) || null;
  const tags = allTags(evals);
  const resultFor = (caseId: string): EvalResult | null => latest?.results.find((r) => r.caseId === caseId) || null;

  const q = query.trim().toLowerCase();
  const cases = evals.cases.filter(
    (c) => (tag === "all" || (c.tags || []).includes(tag)) && (q === "" || c.name.toLowerCase().includes(q) || c.input.toLowerCase().includes(q))
  );

  const open = evals.runs.find((r) => r.id === openRunId) || null;
  if (open) {
    const sheetRun = sheet ? evals.runs.find((r) => r.id === sheet.runId) : null;
    const sheetBase = sheet?.baseId ? evals.runs.find((r) => r.id === sheet.baseId) : null;
    const comparing = compareId ?? (evals.baselineRunId && evals.baselineRunId !== open.id ? evals.baselineRunId : "");
    return (
      <>
        <Matrix
          run={open}
          runs={evals.runs}
          agent={agent}
          caseName={(id) => suite.caseFor(open, id)?.name || id}
          baselineId={evals.baselineRunId}
          compareId={comparing}
          onCompare={setCompareId}
          onBack={() => openRun(null)}
          onSetBaseline={suite.setBaseline}
          onOpenCase={(caseId, baseId) => setSheet({ caseId, runId: open.id, baseId })}
        />
        <CaseSheet
          open={!!sheet}
          onOpenChange={(v) => !v && setSheet(null)}
          kase={sheet ? suite.caseFor(sheetRun, sheet.caseId) : null}
          result={sheet ? sheetRun?.results.find((r) => r.caseId === sheet.caseId) : null}
          baseResult={sheet && sheetBase ? sheetBase.results.find((r) => r.caseId === sheet.caseId) : null}
          onEdit={setEditing}
          onOpenTrace={onOpenTrace}
        />
        {editing && (
          <CaseDialog
            open
            onOpenChange={(v) => !v && setEditing(null)}
            initial={editing === "new" ? null : editing}
            knownTags={tags}
            onSave={suite.saveCase}
          />
        )}
      </>
    );
  }

  return (
    <div className="flex-1 min-w-0 flex flex-col min-h-0">
      <AgentPage
        agent={agent}
        title={`${agent.name} evals`}
        subtitle={
          evals.runs.length === 0
            ? `${evals.cases.length} cases · never run`
            // Non-empty `runs` is exactly the branch condition above.
            : `${evals.cases.length} cases · last run ${latest!.when}`
        }
        description={
          <>
            A small set of cases {agent.name} must keep passing. Run it after every
            change — the score tells you whether the change helped, and every failure
            shows the exact check and the evidence behind it.
          </>
        }
        actions={
          <>
            <GhostButton onClick={() => setEditing("new")}>
              <Plus className="w-3.5 h-3.5" strokeWidth={1.75} /> Case
            </GhostButton>
            <BrandButton onClick={suite.runNow} disabled={!!progress || suite.loading}>
              {progress ? (
                <>Running {progress.done}/{progress.total}</>
              ) : (
                <><Play className="w-3.5 h-3.5" strokeWidth={1.75} /> Run evals</>
              )}
            </BrandButton>
          </>
        }
      >
        {suite.loading ? (
          <div role="status" className="py-14 text-center text-[13px] text-[var(--text-tertiary)]">Loading evals…</div>
        ) : evals.cases.length === 0 ? (
          <div className="py-14 text-center tray-box">
            <CheckCheck className="w-5 h-5 mx-auto mb-2 text-[var(--text-quaternary)]" strokeWidth={1.5} />
            <div className="text-[14px] font-medium text-[var(--text-primary)]">No cases yet</div>
            <div className="text-[12.5px] text-[var(--text-tertiary)] mt-1 max-w-[380px] mx-auto">
              Write one, or open any run in Traces and use <span className="text-[var(--text-primary)]">Add to evals</span> —
              a case built from something that actually went wrong is worth ten invented ones.
            </div>
            <BrandButton className="mx-auto mt-4" onClick={() => setEditing("new")}>
              <Plus className="w-3.5 h-3.5" strokeWidth={1.75} /> Add a case
            </BrandButton>
          </div>
        ) : (
          <>
            <StatsRow evals={evals} latest={latest} base={base} />
            {progress && (
              <div className="mb-5">
                <div className="h-[3px] rounded-full bg-[var(--surface-2)] overflow-hidden">
                  <div
                    className="h-full rounded-full bg-[var(--brand)] transition-[width] duration-200"
                    style={{ width: `${(progress.done / progress.total) * 100}%` }}
                  />
                </div>
              </div>
            )}
            <TrendChart runs={evals.runs} />
            <ModePicker mode={mode} setMode={setMode} caseCount={evals.cases.length} runCount={evals.runs.length} />

            {mode === "checks" ? (
              <>
                <p className="text-[12.5px] leading-[1.6] text-[var(--text-tertiary)] mt-0 mb-3">
                  Suite-wide checks run against every case, on top of whatever that case
                  defines for itself. Guardrails belong here — a tool the agent must never
                  reach for is true of every case, not one.
                </p>
                <CheckList
                  checks={evals.checks}
                  onChange={suite.setChecks}
                  emptyHint="No suite-wide checks — every case grades only on its own."
                />
              </>
            ) : mode === "runs" ? (
              <div className="rounded-[18px] bg-[var(--surface-2)] p-1 pt-2.5">
                <div className="flex items-center gap-3 px-3 pb-2">
                  <span className="flex-1 section-label">Run</span>
                  <span className="hidden sm:block w-[92px] shrink-0 section-label">Score</span>
                  <span className="w-[60px] shrink-0 section-label text-center">Δ</span>
                  <span className="hidden md:block w-[64px] shrink-0 section-label text-right">Cost</span>
                  <span className="w-[80px] shrink-0 section-label text-right">When</span>
                  <span className="w-[92px] shrink-0" />
                </div>
                <div className="rounded-[14px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] shadow-lift overflow-hidden divide-y divide-[var(--hairline)] empty:hidden">
                  {evals.runs.map((r) => (
                    <RunRow
                      key={r.id}
                      run={r}
                      base={base}
                      isBaseline={r.id === evals.baselineRunId}
                      onOpen={openRun}
                      onSetBaseline={suite.setBaseline}
                    />
                  ))}
                </div>
                {evals.runs.length === 0 && (
                  <div className="py-12 text-center text-[13px] text-[var(--text-tertiary)] rounded-[14px] bg-[var(--surface)] ring-1 ring-[var(--hairline)]">
                    Never run. Hit <span className="text-[var(--text-primary)]">Run evals</span> to get a first number.
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 flex-wrap pb-4">
                  <div className="flex items-center gap-1.5">
                    {["all", ...tags].map((t) => (
                      <button
                        key={t}
                        onClick={() => setTag(t)}
                        className={`press h-7 px-2.5 rounded-full text-[12.5px] font-medium ${
                          tag === t
                            ? "bg-[var(--brand)] text-[var(--brand-foreground)]"
                            : "ring-1 ring-[var(--hairline-strong)] text-[var(--text-secondary)] hover:bg-[var(--accent-hover)]"
                        }`}
                      >
                        {t === "all" ? "All" : t}
                      </button>
                    ))}
                  </div>
                  <div className="flex-1" />
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[var(--text-tertiary)]" strokeWidth={1.75} />
                    <input
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Search cases"
                      className="w-[150px] h-8 pl-8 pr-3 rounded-full bg-[var(--surface)] ring-1 ring-[var(--hairline-strong)] text-[13px] text-[var(--text-primary)] outline-none focus:ring-[var(--hairline-focus)] placeholder:text-[var(--text-quaternary)] transition-shadow"
                    />
                  </div>
                </div>

                {cases.length === 0 ? (
                  <div className="py-12 text-center text-[13px] text-[var(--text-tertiary)] tray-box">
                    No cases match your filters.
                  </div>
                ) : (
                  <div className="tray-box overflow-hidden divide-y divide-[var(--hairline)]">
                    {cases.map((c) => (
                      <CaseRow
                        key={c.id}
                        kase={c}
                        evals={evals}
                        result={resultFor(c.id)}
                        // a case the latest run has no result for (added since) opens in the editor
                        onOpen={() => latest && resultFor(c.id) ? setSheet({ caseId: c.id, runId: latest.id, baseId: base?.id || null }) : setEditing(c)}
                        onEdit={setEditing}
                        onDelete={suite.deleteCase}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </AgentPage>

      <CaseSheet
        open={!!sheet}
        onOpenChange={(v) => !v && setSheet(null)}
        kase={sheet ? evals.cases.find((c) => c.id === sheet.caseId) : null}
        result={sheet ? evals.runs.find((r) => r.id === sheet.runId)?.results.find((r) => r.caseId === sheet.caseId) : null}
        baseResult={sheet?.baseId ? evals.runs.find((r) => r.id === sheet.baseId)?.results.find((r) => r.caseId === sheet.caseId) : null}
        onEdit={setEditing}
        onOpenTrace={onOpenTrace}
      />

      {editing && (
        <CaseDialog
          open
          onOpenChange={(v) => !v && setEditing(null)}
          initial={editing === "new" ? null : editing}
          knownTags={tags}
          onSave={suite.saveCase}
        />
      )}
    </div>
  );
}

